import { FormikErrors, FormikTouched, FormikValues } from 'formik';
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../authentication/authService';
import { StoreContext } from '../../context/Provider';
import { useUserManager } from '../../hooks/usersManager/useUserManager';
import { IUserData } from '../../interfaces/interfaceUserMenu';
import { validationsPersonalData } from '../../layout/components/Header/validationsPersonalData';
import useCustomFormik from '../../pages/Catalogs/components/forms/utils/formik_catalog_Config';
import { validateUserWebPermission } from '../../utils/utils';
import { useSettings } from './../../hooks/settings/useSettings';
import { AccessListValues } from '../../permitsList';
import { createNavItems } from '../../routes/HeadersRoutes';
import { INavItem } from '../../interfaces';

export interface IHeaderServiceManager {
  showPopover: boolean;
  permitUrl: number;

  menuSelected: string | undefined;
  showProfileModal: boolean;
  user: IUserData;
  patchUserManagerState: any;
  navItems: INavItem[];
  sidebarItems: INavItem[];

  onSelectedNavItem: (_path: string) => void;

  setMenuSelected: Dispatch<SetStateAction<string | undefined>>;
  setShowPopover: (any: boolean) => void;
  closePersonalModal: () => void;

  formikPersonalData: any;
  openProfileModal: (any?: any) => void;
  logout: (any?: any) => void;
  isFormFieldValid: (
    name: string,
    errors: FormikErrors<FormikValues>,
    touched: FormikTouched<FormikValues>
  ) => boolean;
  getFormErrorMessage: (
    name: string,
    errors: FormikErrors<FormikValues>,
    touched: FormikTouched<FormikValues>
  ) => React.ReactNode;
  isMenuOpen: boolean;
  setIsMenuOpen: Dispatch<SetStateAction<boolean>>;
  menuItemSelected: INavItem | undefined;
}

export const HeaderServiceManager = createContext<IHeaderServiceManager>(
  {} as IHeaderServiceManager
);

const useHeaderProvider = () => {
  const { getPersonalData } = useSettings();
  const { patchUserAndUpdateUserLogged } = useUserManager();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const [permitUrl, setPermitUrl] = useState<number>(0);

  const [menuSelected, setMenuSelected] = useState<string | undefined>('');
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  const [inputs, setInputs] = useState<any>();
  const [sidebarItems, setSidebarItems] = useState<any[]>([]);
  const [menuItemSelected, setMenuItemSelected] = useState<
    INavItem | undefined
  >();

  const context = useContext(StoreContext);
  const { getSettingsState, patchUserManagerState } = context;

  const [user, setUser] = useState<IUserData>({
    userName: '',
    firstName: '',
    surName: '',
    lastName: '',
    email: '',
    rol: '',
    roleName: '',
    phone: '',
    modules: [],
  });

  const navItems = createNavItems(getSettingsState.data);

  useEffect(() => {
    const path =
      window.location.pathname === '/'
        ? '/inventario'
        : window.location.pathname;
    onSelectedNavItem(path);
    getPersonalData();
  }, []);

  useEffect(() => {
    if (
      getSettingsState?.data !== undefined &&
      getSettingsState?.data !== null
    ) {
      const data = getSettingsState?.data;
      setUser(data);
      setInputs(data);
      localStorage.setItem('permitions_user', data['permissionValue']);
      localStorage.setItem('actionValue_user', data['roleActionValue']);
      localStorage.setItem('userId', data['userId']);
      //debugger;
      const validateWebUser = validateUserWebPermission();

      if (validateWebUser === false && !data.allowViewHistorySam) {
        navigate('/denegado');
        return;
      }
      if (validateWebUser === false && data.allowViewHistorySam) {
        navigate('/history-sam');
      }
      if (validateWebUser === true) {
        navigate('/');
      }
    }
  }, [getSettingsState?.data]);

  function onSelectedNavItem(_path: string) {
    const selectedNav = navItems.find(nav => {
      if (nav.to === _path) return true;
      return nav.items?.some(item => item.to === _path);
    });

    if (!selectedNav) {
      console.warn('Ruta no encontrada en navItems:', _path);
      return;
    }

    const _pathSelected =
      selectedNav.items.length > 0
        ? selectedNav.items.find(item => item.to === _path)?.to ||
          selectedNav.items[0].to
        : selectedNav.to;
    setPermitUrl(selectedNav.permit);
    setMenuSelected(selectedNav.parent);
    setSidebarItems(selectedNav.items);
    navigate(_pathSelected);
  }

  const formikPersonalData = useCustomFormik(
    inputs,
    validationsPersonalData,
    handleSubmitForm
  );

  function isFormFieldValid(
    name: string,
    errors: FormikErrors<FormikValues>,
    touched: FormikTouched<FormikValues>
  ) {
    return !!(touched[name] && errors[name]);
  }

  function getFormErrorMessage(
    name: string,
    errors: FormikErrors<FormikValues>,
    touched: FormikTouched<FormikValues>
  ) {
    const error = errors[name];
    const errorMessage = typeof error === 'string' ? error : '';
    return (
      isFormFieldValid(name, errors, touched) && (
        <small className="p-error">{errorMessage}</small>
      )
    );
  }

  useEffect(() => {
    const validateAndSanitizeField = (fieldName: string) => {
      const fieldValue = formikPersonalData?.values?.[fieldName] || '';
      const onlyText = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]*$/;

      if (!onlyText.test(fieldValue)) {
        const sanitizedValue = fieldValue.replace(
          /[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g,
          ''
        );
        formikPersonalData.setFieldValue(fieldName, sanitizedValue);
      }
    };

    validateAndSanitizeField('firstName');
    validateAndSanitizeField('lastName');
  }, [
    formikPersonalData?.values?.['firstName'],
    formikPersonalData?.values?.['lastName'],
  ]);

  async function handleSubmitForm(values: any) {
    const userObj = {
      firstName: values['firstName'],
      jobPositionId: values['jobPositionId'],
      lastName: values['lastName'],
      phone: values['phone'].toString(),
      roleId: values['roleId'].toString(),
    };

    const response = await patchUserAndUpdateUserLogged(
      userObj,
      values['userId']
    );
    if (response.status === 200) {
      setShowProfileModal(false);
    }
  }

  function closePersonalModal() {
    formikPersonalData.resetForm();
    setShowProfileModal(false);
  }

  function logout() {
    authService.logout();
  }

  function openProfileModal() {
    setShowProfileModal(true);
  }
  return {
    showPopover,
    permitUrl,

    menuSelected,
    showProfileModal,
    user,
    patchUserManagerState,
    sidebarItems,
    setMenuSelected,
    setShowPopover,
    closePersonalModal,

    formikPersonalData,
    openProfileModal,
    logout,
    isFormFieldValid,
    getFormErrorMessage,
    navItems,
    onSelectedNavItem,
    isMenuOpen,
    setIsMenuOpen,
    menuItemSelected,
  };
};

export const HeaderProvider = (props: any) => {
  const headerValues = useHeaderProvider();
  return (
    <HeaderServiceManager.Provider value={headerValues}>
      {props.children}
    </HeaderServiceManager.Provider>
  );
};
